/**
 * Stage 21 · Marketplace Admin Centre.
 * Suntronix admin manages: categories, products, bundles, and per-customer settings.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Package, Layers, Grid3x3, Settings, Plus, X, ToggleLeft, ToggleRight, Save,
} from "lucide-react";

const TABS = [
  { key: "products",   label: "Products",   icon: Package },
  { key: "categories", label: "Categories", icon: Grid3x3 },
  { key: "bundles",    label: "Bundles",    icon: Layers },
  { key: "settings",   label: "Customer catalogues", icon: Settings },
];

export default function MarketplaceAdmin() {
  const [tab, setTab] = useState("products");
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => {}); }, []);

  // Client-side guard: customer roles reach this page only via direct URL.
  // Render a clear notice rather than the admin shell so intent is unambiguous.
  if (me && me.master_company_id) {
    return (
      <div className="max-w-lg bg-white border border-slate-200 rounded-md p-8 text-center" data-testid="mkta-forbidden">
        <div className="text-amber-600 text-3xl">🔒</div>
        <h1 className="text-xl font-heading font-semibold text-slate-900 mt-3">Marketplace administration is internal</h1>
        <p className="text-sm text-slate-600 mt-2">Please head to <a className="text-slate-900 underline" href="/marketplace">the marketplace</a> instead.</p>
      </div>
    );
  }
  return (
    <div className="max-w-6xl space-y-5" data-testid="mkt-admin">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">
          Administration · Marketplace
        </div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">
          Marketplace admin centre
        </h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Publish products, bundle kits, and configure which customer sees what — with pricing visibility,
          approval mode, inventory preference and commercial submit flow.
        </p>
      </div>

      <nav className="flex flex-wrap gap-1 border-b border-slate-200" role="tablist">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`mkta-tab-${key}`}
            className={"px-3 py-2 text-sm inline-flex items-center gap-1.5 border-b-2 -mb-px " +
              (tab === key ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800")}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </nav>

      {tab === "products"   && <ProductsAdmin />}
      {tab === "categories" && <CategoriesAdmin />}
      {tab === "bundles"    && <BundlesAdmin />}
      {tab === "settings"   && <SettingsAdmin />}
    </div>
  );
}

/* ================================================ Products */
function ProductsAdmin() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = () => api.get("/marketplace/products", { params: { limit: 500 } }).then(({ data }) => setItems(data.items || []));
  useEffect(() => { load().catch(() => {}); api.get("/marketplace/categories").then(({ data }) => setCats(data.items || [])); }, []);
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" data-testid="mkta-product-new">
          <Plus className="h-4 w-4" /> New product
        </button>
      </div>
      {showForm && <ProductForm cats={cats} onCancel={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left py-2 px-3">Name</th>
              <th className="text-left py-2 px-3">Category</th>
              <th className="text-left py-2 px-3">SKU</th>
              <th className="text-right py-2 px-3">Price (INR)</th>
              <th className="text-left py-2 px-3">Availability</th>
              <th className="text-left py-2 px-3">Active</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6} className="py-6 text-center text-slate-500">No products yet.</td></tr>
            ) : items.map((p) => (
              <tr key={p.id} className="border-t border-slate-100">
                <td className="py-2 px-3"><div className="text-slate-900">{p.name}</div><div className="text-[10px] text-slate-500">{p.brand}</div></td>
                <td className="py-2 px-3 text-slate-600">{p.category_code}</td>
                <td className="py-2 px-3 text-slate-500 font-mono">{p.sku || "—"}</td>
                <td className="py-2 px-3 text-right tabular-nums">{p.price_inr != null ? `₹${Number(p.price_inr).toLocaleString("en-IN")}` : "—"}</td>
                <td className="py-2 px-3 text-slate-600">{p.availability_status}</td>
                <td className="py-2 px-3">
                  <button onClick={async () => {
                    // Optimistic flip so the button reflects the new state immediately.
                    setItems((prev) => prev.map((x) => x.id === p.id ? { ...x, active: !x.active } : x));
                    try { await api.patch(`/marketplace/products/${p.id}`, { active: !p.active }); load(); }
                    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
                  }}
                    className={"text-xs px-2 py-0.5 rounded border " + (p.active ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-slate-50 border-slate-200 text-slate-600")}
                    data-testid={`mkta-product-toggle-${p.id}`}>
                    {p.active ? "Active" : "Inactive"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProductForm({ cats, onCancel, onCreated }) {
  const [f, setF] = useState({
    category_code: cats[0]?.code || "laptop", name: "", brand: "", model: "", sku: "",
    description: "", warranty_months: 24, availability_status: "available", price_inr: "",
    lifecycle_stage: "current", active: true,
  });
  const save = async () => {
    try {
      const payload = { ...f, price_inr: f.price_inr === "" ? null : Number(f.price_inr) };
      await api.post("/marketplace/products", payload);
      toast.success("Product published");
      onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2" data-testid="mkta-product-form">
      <div className="grid grid-cols-2 gap-2">
        <label>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Name</div>
          <input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="mkta-product-name" />
        </label>
        <label>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Category</div>
          <select className="input" value={f.category_code} onChange={(e) => setF({ ...f, category_code: e.target.value })} data-testid="mkta-product-cat">
            {cats.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Brand</div>
          <input className="input" value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} data-testid="mkta-product-brand" />
        </label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Model</div>
          <input className="input" value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} />
        </label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">SKU</div>
          <input className="input" value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} data-testid="mkta-product-sku" />
        </label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Price (INR)</div>
          <input type="number" className="input" value={f.price_inr} onChange={(e) => setF({ ...f, price_inr: e.target.value })} data-testid="mkta-product-price" />
        </label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Warranty (months)</div>
          <input type="number" className="input" value={f.warranty_months} onChange={(e) => setF({ ...f, warranty_months: Number(e.target.value) })} />
        </label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Availability</div>
          <select className="input" value={f.availability_status} onChange={(e) => setF({ ...f, availability_status: e.target.value })} data-testid="mkta-product-avail">
            <option value="available">Available</option><option value="limited">Limited</option><option value="procurement_required">Procure</option>
            <option value="backordered">Backordered</option><option value="unavailable">Unavailable</option>
            <option value="country_restricted">Country restricted</option><option value="approval_required">Approval required</option>
          </select>
        </label>
        <label className="col-span-2"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Description</div>
          <textarea className="input min-h-[70px]" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        </label>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white" onClick={onCancel}>Cancel</button>
        <button className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" onClick={save} data-testid="mkta-product-save">Publish</button>
      </div>
    </div>
  );
}

/* ================================================ Categories */
function CategoriesAdmin() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({ code: "", name: "", sort_order: 100, active: true });
  const load = () => api.get("/marketplace/categories").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load().catch(() => {}); }, []);
  const save = async () => {
    try {
      await api.post("/marketplace/categories", f);
      toast.success("Category created");
      setShowForm(false); setF({ code: "", name: "", sort_order: 100, active: true }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" data-testid="mkta-cat-new">
          <Plus className="h-4 w-4" /> New category
        </button>
      </div>
      {showForm && (
        <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2" data-testid="mkta-cat-form">
          <div className="grid grid-cols-3 gap-2">
            <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Code</div>
              <input className="input" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="mkta-cat-code" placeholder="laptop" />
            </label>
            <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Name</div>
              <input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="mkta-cat-name" placeholder="Laptops" />
            </label>
            <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Sort order</div>
              <input type="number" className="input" value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: Number(e.target.value) })} />
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" onClick={save} data-testid="mkta-cat-save">Save</button>
          </div>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr><th className="text-left py-2 px-3">Code</th><th className="text-left py-2 px-3">Name</th><th className="text-right py-2 px-3">Sort</th><th className="text-left py-2 px-3">Active</th></tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={4} className="py-6 text-center text-slate-500">No categories yet.</td></tr>
            ) : items.map((c) => (
              <tr key={c.id} className="border-t border-slate-100">
                <td className="py-2 px-3 font-mono text-slate-900">{c.code}</td>
                <td className="py-2 px-3 text-slate-800">{c.name}</td>
                <td className="py-2 px-3 text-right tabular-nums">{c.sort_order}</td>
                <td className="py-2 px-3"><span className={c.active ? "text-emerald-700 text-xs" : "text-slate-500 text-xs"}>{c.active ? "Active" : "Inactive"}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ================================================ Bundles */
function BundlesAdmin() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = () => api.get("/marketplace/bundles").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load().catch(() => {}); api.get("/marketplace/products", { params: { limit: 500 } }).then(({ data }) => setProducts(data.items || [])); }, []);
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" data-testid="mkta-bundle-new">
          <Plus className="h-4 w-4" /> New kit
        </button>
      </div>
      {showForm && <BundleForm products={products} onCancel={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.length === 0 ? (
          <div className="col-span-2 bg-white border border-slate-200 rounded-md p-6 text-center text-sm text-slate-500">No kits yet.</div>
        ) : items.map((b) => (
          <div key={b.id} className="bg-white border border-slate-200 rounded-md p-3" data-testid={`mkta-bundle-${b.id}`}>
            <div className="flex items-start gap-2">
              <div className="text-2xl">🧰</div>
              <div className="flex-1">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{b.kind?.replace(/_/g, " ")}</div>
                <div className="text-sm font-medium text-slate-900">{b.name}</div>
                {b.description && <p className="text-xs text-slate-600 mt-1">{b.description}</p>}
              </div>
              <div className="text-[10px] text-slate-500">v{b.version || 1}</div>
            </div>
            <div className="mt-2 text-xs text-slate-600 space-y-0.5">
              {(b.items || []).map((it, i) => {
                const p = products.find((x) => x.id === it.product_id);
                return <div key={i} className="pl-1">• {p?.name || it.product_id?.slice(0, 8)} × {it.qty || 1}</div>;
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BundleForm({ products, onCancel, onCreated }) {
  const [f, setF] = useState({ name: "", description: "", kind: "employee_kit", device_profile_code: "", items: [{ product_id: products[0]?.id || "", qty: 1 }], services: [] });
  const setItem = (i, upd) => setF((f) => ({ ...f, items: f.items.map((x, j) => j === i ? { ...x, ...upd } : x) }));
  const addItem = () => setF((f) => ({ ...f, items: [...f.items, { product_id: products[0]?.id || "", qty: 1 }] }));
  const removeItem = (i) => setF((f) => ({ ...f, items: f.items.filter((_, j) => j !== i) }));
  const save = async () => {
    try {
      await api.post("/marketplace/bundles", f);
      toast.success("Kit created");
      onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2" data-testid="mkta-bundle-form">
      <div className="grid grid-cols-2 gap-2">
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Name</div>
          <input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="mkta-bundle-name" />
        </label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Kind</div>
          <select className="input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="mkta-bundle-kind">
            <option value="employee_kit">Employee kit</option><option value="project_kit">Project kit</option>
            <option value="remote_kit">Remote-work kit</option><option value="office_kit">Office kit</option><option value="executive_kit">Executive kit</option>
          </select>
        </label>
        <label className="col-span-2"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Description</div>
          <input className="input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        </label>
      </div>
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1 mt-2">Items</div>
        {f.items.map((it, i) => (
          <div key={i} className="grid grid-cols-6 gap-2 items-center mb-1">
            <select className="input col-span-4" value={it.product_id} onChange={(e) => setItem(i, { product_id: e.target.value })} data-testid={`mkta-bundle-item-${i}`}>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <input type="number" className="input" value={it.qty} onChange={(e) => setItem(i, { qty: Number(e.target.value) })} />
            <button onClick={() => removeItem(i)} className="h-9 rounded-md border border-slate-200 text-rose-600 text-xs">Remove</button>
          </div>
        ))}
        <button onClick={addItem} className="text-xs text-slate-700 hover:text-slate-900 inline-flex items-center gap-1"><Plus className="h-3 w-3" /> Add item</button>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white" onClick={onCancel}>Cancel</button>
        <button className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" onClick={save} data-testid="mkta-bundle-save">Publish kit</button>
      </div>
    </div>
  );
}

/* ================================================ Settings per customer */
function SettingsAdmin() {
  const [masters, setMasters] = useState([]);
  const [mc, setMc] = useState("");
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api.get("/master-companies").then(({ data }) => { setMasters(data.items || []); setMc(current => current || data.items?.[0]?.id || ""); });
    api.get("/marketplace/products", { params: { limit: 500 } }).then(({ data }) => setProducts(data.items || []));
  }, []);
  useEffect(() => {
    if (!mc) return;
    api.get("/marketplace/settings", { params: { master_company_id: mc } }).then(({ data }) => setSettings(data));
  }, [mc]);

  const save = async () => {
    try {
      await api.post("/marketplace/settings", { ...settings, master_company_id: mc });
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!settings) return <div className="text-sm text-slate-500">Loading…</div>;

  const toggleProduct = (pid) => {
    const cur = new Set(settings.allowed_product_ids || []);
    cur.has(pid) ? cur.delete(pid) : cur.add(pid);
    setSettings({ ...settings, allowed_product_ids: [...cur] });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <select className="input min-w-[240px]" value={mc} onChange={(e) => setMc(e.target.value)} data-testid="mkta-settings-mc">
          {masters.map((m) => <option key={m.id} value={m.id}>{m.name}{m.is_demo ? " · DEMO" : ""}</option>)}
        </select>
        <button className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" onClick={save} data-testid="mkta-settings-save">
          <Save className="h-4 w-4" /> Save
        </button>
      </div>

      <section className="bg-white border border-slate-200 rounded-md p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <ToggleRow label="Marketplace enabled" checked={!!settings.enabled} onChange={(v) => setSettings({ ...settings, enabled: v })} testId="mkta-set-enabled" />
        <ToggleRow label="Show pricing to all customer users" checked={!!settings.price_visibility_public} onChange={(v) => setSettings({ ...settings, price_visibility_public: v })} testId="mkta-set-price" />
        <ToggleRow label="Prefer customer inventory first" checked={!!settings.inventory_first} onChange={(v) => setSettings({ ...settings, inventory_first: v })} testId="mkta-set-inventory-first" />
        <ToggleRow label="Approval required before submit" checked={!!settings.approval_required} onChange={(v) => setSettings({ ...settings, approval_required: v })} testId="mkta-set-approval" />
        <label className="md:col-span-2">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Submit cart as</div>
          <select className="input" value={settings.submit_as || "request"} onChange={(e) => setSettings({ ...settings, submit_as: e.target.value })} data-testid="mkta-set-submit-as">
            <option value="request">Request (default)</option>
            <option value="quotation">Quotation</option>
            <option value="order">Order (draft)</option>
          </select>
        </label>
      </section>

      <section className="bg-white border border-slate-200 rounded-md">
        <div className="p-3 border-b border-slate-200 flex items-center gap-2">
          <h3 className="text-sm font-medium text-slate-900">Approved products for this customer</h3>
          <span className="ml-auto text-[10px] text-slate-500">
            {(settings.allowed_product_ids || []).length || "All"} selected · empty means every active product
          </span>
        </div>
        <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-1 max-h-[400px] overflow-y-auto">
          {products.map((p) => {
            const on = (settings.allowed_product_ids || []).includes(p.id);
            return (
              <label key={p.id} className="flex items-center gap-2 py-1.5 cursor-pointer text-sm" data-testid={`mkta-allow-${p.sku || p.id}`}>
                <input type="checkbox" checked={on} onChange={() => toggleProduct(p.id)} className="rounded" />
                <span className="text-slate-900">{p.name}</span>
                <span className="text-[10px] text-slate-500 font-mono">{p.sku}</span>
                <span className="text-[10px] text-slate-500 ml-auto">{p.category_code}</span>
              </label>
            );
          })}
        </div>
      </section>
    </div>
  );
}
function ToggleRow({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center justify-between gap-2 cursor-pointer">
      <span className="text-sm text-slate-800">{label}</span>
      <button onClick={() => onChange(!checked)} data-testid={testId} className="focus:outline-none">
        {checked ? <ToggleRight className="h-6 w-6 text-emerald-600" /> : <ToggleLeft className="h-6 w-6 text-slate-400" />}
      </button>
    </label>
  );
}
